import { Phone, MapPin, Clock, Siren } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import GHLForm from "@/components/GHLForm";
import SEO from "@/components/SEO";
import { businessSchema } from "@/data/schema";

const emergencySteps = [
  { n: "01", text: "Call (318) 312-2112 — a live dispatcher answers at any hour, day or night." },
  { n: "02", text: "Describe the symptoms: no cooling, no heat, strange noises, leaks, or a burning smell." },
  { n: "03", text: "If you smell gas or burning electrical, shut the system off at the breaker and tell us immediately." },
  { n: "04", text: "A certified technician dispatches from Trout with a stocked truck and an honest arrival window." },
];

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact McCann & Sons HVAC | Call (318) 312-2112"
        description="Reach McCann and Sons HVAC 24/7 at (318) 312-2112 or 164 Dixie Lane, Trout, LA 71371. Emergency repairs answered live. Request service online."
        path="/contact"
      />
      {/* PAGE HERO */}
      <section className="relative bg-navy-deep overflow-hidden grain-overlay">
        <div className="absolute inset-0 hero-grid-bg" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-28">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-frost mb-5">
            <MaskedLine delay={0.1}>We Answer 24 Hours A Day</MaskedLine>
          </p>
          <h1 className="font-display text-5xl sm:text-7xl font-black uppercase tracking-tight leading-[0.92] text-white">
            <MaskedLine delay={0.2}>Get A Technician</MaskedLine>
            <MaskedLine delay={0.35}>
              <span className="text-ember">On The Way</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="bg-slate-50 py-20 lg:py-32" data-testid="contact-main-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* LEFT: INFO */}
          <div className="space-y-6">
            <Reveal>
              <div className="bg-navy-deep text-white rounded-sm p-8 grain-overlay relative overflow-hidden" data-testid="contact-info-card">
                <div className="hero-grid-bg absolute inset-0" />
                <div className="relative space-y-7">
                  <div className="flex items-start gap-4">
                    <MapPin className="h-6 w-6 text-ember shrink-0 mt-1" />
                    <div>
                      <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-frost mb-2">Shop Address</h3>
                      <p className="text-lg font-semibold">164 Dixie Lane</p>
                      <p className="text-slate-300">Trout, Louisiana 71371</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Phone className="h-6 w-6 text-ember shrink-0 mt-1" />
                    <div>
                      <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-frost mb-2">Direct Line</h3>
                      <a
                        data-testid="contact-phone-link"
                        href="tel:3183122112"
                        className="text-2xl font-display font-bold tracking-wide hover:text-ember transition-colors"
                      >
                        (318) 312-2112
                      </a>
                      <p className="text-slate-300 text-sm mt-1">Tap to call — answered live, 24/7.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="h-6 w-6 text-ember shrink-0 mt-1" />
                    <div>
                      <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-frost mb-2">Operating Hours</h3>
                      <p className="text-lg font-semibold">Open 24 Hours</p>
                      <p className="text-slate-300 text-sm">7 days a week, 365 days a year — including holidays.</p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="bg-white border-t-4 border-t-ember border-x border-b border-slate-200 rounded-sm p-8" data-testid="emergency-instructions-card">
                <div className="flex items-center gap-3 mb-6">
                  <Siren className="h-6 w-6 text-ember" />
                  <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-navy">
                    Emergency? Do This First
                  </h2>
                </div>
                <ol className="space-y-5">
                  {emergencySteps.map((s) => (
                    <li key={s.n} className="flex gap-4">
                      <span className="font-mono text-sm text-ember font-bold shrink-0">{s.n}</span>
                      <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
                    </li>
                  ))}
                </ol>
                <a
                  data-testid="contact-emergency-call-btn"
                  href="tel:3183122112"
                  className="mt-8 inline-flex w-full items-center justify-center gap-2 bg-ember hover:bg-ember-dark text-navy-deep font-bold px-6 py-4 rounded-sm transition-all hover:-translate-y-0.5 active:scale-95"
                >
                  <Phone className="h-4 w-4" />
                  Emergency Repair — Call Now
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.18}>
              <p className="text-sm text-slate-500 leading-relaxed px-1">
                Serving Trout, Jena, Urania, Olla, Tullos, Alexandria, Pineville, Ball, Pollock, Dry Prong,
                Georgetown, Winnfield, and surrounding Central Louisiana communities. For non-urgent
                estimates, installations, and maintenance plans, the form on this page is the fastest way
                to reach our scheduling team.
              </p>
            </Reveal>
          </div>

          {/* RIGHT: FORM */}
          <Reveal delay={0.1}>
            <div>
              <h2 className="font-display text-3xl font-bold uppercase tracking-wide text-navy mb-6">
                Send A Service Request
              </h2>
              <GHLForm />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
